import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { tl, stat } from "@/lib/i18nFormat";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, LoadingBlock, EmptyState, StatusPill } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { BookOpen, Search, FileText, CheckCircle2, Paperclip, Plus, Send, History } from "lucide-react";

import { useI18n } from "@/context/I18nContext";

const STATUS: Record<string, string> = { draft: "info", published: "good", superseded: "warn", archived: "inactive" };

export default function Knowledge() {
  const {
    t: tI18n
  } = useI18n();

  const { hasPerm } = useAuth();
  const canAuthor = hasPerm("kb.author");
  const canAssign = hasPerm("kb.assign");
  const [tab, setTab] = useState("library");
  const [docs, setDocs] = useState<any[]>([]);
  const [assigned, setAssigned] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [active, setActive] = useState<any>(null);
  const [detail, setDetail] = useState<any>(null);
  const [composeOpen, setComposeOpen] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([api.get("/kb/documents"), api.get("/kb/reading/assigned")])
      .then(([d, a]) => { setDocs(d.data.documents); setAssigned(a.data.assignments); })
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const runSearch = async () => {
    if (q.trim().length < 2) { load(); return; }
    const r = await api.get(`/kb/search?q=${encodeURIComponent(q.trim())}`);
    setDocs(r.data.results);
  };

  const openDoc = async (d: any) => {
    setActive(d);
    const r = await api.get(`/kb/documents/${d.id}`);
    setDetail(r.data);
  };

  const ackAssignment = async (a: any) => {
    await api.post(`/kb/reading/${a.assignment_id}/ack`);
    toast.success(tl("Acknowledged v{n}", { n: a.version_no }));
    load();
  };

  const downloadAttachment = async (attId: string, filename: string) => {
    try {
      const r = await api.get(`/kb/attachments/${attId}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const link = document.createElement("a");
      link.href = url; link.download = filename; link.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Not authorized"); }
  };

  const unacked = assigned.filter((a) => !a.acknowledged).length;

  return (
    (<div>
      <PageHeader
        title="Knowledge"
        subtitle="SOPs, policies, and required reading — versioned and permission-scoped"
        testId="knowledge-header"
        actions={canAuthor && (
          <ComposeDialog open={composeOpen} setOpen={setComposeOpen} onDone={load} />
        )}
      />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="library" data-testid="kb-tab-library">{tI18n("Library")}</TabsTrigger>
          <TabsTrigger value="assigned" data-testid="kb-tab-assigned">{tI18n("Required Reading")}{unacked > 0 ? ` (${unacked})` : ""}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="library">
          <div className="mb-4 flex gap-2">
            <Input placeholder={tI18n("Search titles, descriptions, tags…")} value={q}
              onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && runSearch()}
              data-testid="kb-search-input" />
            <Button variant="outline" onClick={runSearch} data-testid="kb-search-button"><Search size={16} /></Button>
          </div>
          {loading ? <LoadingBlock /> : docs.length === 0 ? (
            <EmptyState icon={<BookOpen size={28} />} title="No documents" />
          ) : (
            <div className="grid gap-3 md:grid-cols-2">
              {docs.map((d) => (
                <Card key={d.id} className="cursor-pointer p-4 hover:border-brand/40" onClick={() => openDoc(d)} data-testid={`kb-doc-${d.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2 font-display font-semibold"><FileText size={16} className="text-brand" />{d.title}</div>
                    <StatusPill status={STATUS[d.status] || "info"}>{stat(d.status)}</StatusPill>
                  </div>
                  {d.description && <p className="mt-1 text-sm text-muted-foreground">{d.description}</p>}
                  <div className="mt-2 flex flex-wrap gap-1">
                    {(d.tags || []).map((t: string) => <span key={t} className="rounded bg-secondary px-1.5 py-0.5 text-[11px]">{t}</span>)}
                    <span className="ml-auto text-[11px] text-muted-foreground">v{d.current_version_no}</span>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="assigned">
          {assigned.length === 0 ? <EmptyState icon={<CheckCircle2 size={28} />} title="No required reading" /> : (
            <Card className="overflow-hidden" data-testid="kb-assigned-list">
              {assigned.map((a) => (
                <div key={a.assignment_id} data-testid={`kb-assignment-${a.assignment_id}`} className="flex items-center justify-between gap-3 border-b border-border px-4 py-3 last:border-0">
                  <div>
                    <div className="font-medium">{a.title}</div>
                    <div className="text-xs text-muted-foreground">{tI18n("Version")}{" "}{a.version_no}{a.effective_date ? ` · ${tl("effective")} ${a.effective_date}` : ""}</div>
                  </div>
                  {a.acknowledged
                    ? <span className="flex items-center gap-1 text-xs text-status-good"><CheckCircle2 size={14} />{tI18n("Acknowledged v")}{a.version_no}</span>
                    : <Button size="sm" onClick={() => ackAssignment(a)} data-testid={`kb-ack-${a.assignment_id}`}>{tI18n("Acknowledge")}</Button>}
                </div>
              ))}
            </Card>
          )}
        </TabsContent>
      </Tabs>
      <Dialog open={!!active} onOpenChange={(v) => { if (!v) { setActive(null); setDetail(null); } }}>
        <DialogContent className="max-w-2xl" data-testid="kb-doc-dialog">
          <DialogHeader><DialogTitle>{active?.title}</DialogTitle></DialogHeader>
          {!detail ? <LoadingBlock /> : (
            <div className="max-h-[70vh] space-y-4 overflow-y-auto">
              <div className="whitespace-pre-wrap rounded-md border border-border bg-muted/20 p-3 text-sm">
                {detail.published_version?.body || detail.document?.draft?.body || "No content yet."}
              </div>
              <DocMeta detail={detail} download={downloadAttachment} docId={active.id} />
              {canAuthor && detail.document?.status !== "archived" && (
                <AuthorPanel docId={active.id} detail={detail} canAssign={canAssign} onChange={() => openDoc(active)} onListChange={load} />
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>)
  );
}

const DocMeta: React.FC<{ detail: any; download: (id: string, fn: string) => void; docId: string }> = ({ detail, download, docId }) => {
  const {
    t
  } = useI18n();

  const [atts, setAtts] = useState<any[]>([]);
  useEffect(() => { api.get(`/kb/documents/${docId}/attachments`).then((r) => setAtts(r.data.attachments)).catch(() => {}); }, [docId]);
  return (
    (<div className="grid gap-3 text-sm sm:grid-cols-2">
      <div>
        <div className="mb-1 flex items-center gap-1 text-xs font-semibold uppercase text-muted-foreground"><History size={12} />{t("Versions")}</div>
        {(detail.versions || []).map((v: any) => (
          <div key={v.id} className="text-xs text-muted-foreground">v{v.version_no} · {v.effective_date} · {v.change_summary || "—"}</div>
        ))}
        {(!detail.versions || detail.versions.length === 0) && <div className="text-xs text-muted-foreground">{t("Not published yet")}</div>}
      </div>
      <div>
        <div className="mb-1 flex items-center gap-1 text-xs font-semibold uppercase text-muted-foreground"><Paperclip size={12} />{t("Attachments")}</div>
        {atts.length === 0 ? <div className="text-xs text-muted-foreground">{t("None")}</div> : atts.map((a) => (
          <button key={a.id} onClick={() => download(a.id, a.filename)} className="block text-xs text-brand hover:underline" data-testid={`kb-att-${a.id}`}>{a.filename}</button>
        ))}
      </div>
    </div>)
  );
};

const AuthorPanel: React.FC<{ docId: string; detail: any; canAssign: boolean; onChange: () => void; onListChange: () => void }> = ({ docId, detail, canAssign, onChange, onListChange }) => {
  const {
    t
  } = useI18n();

  const [body, setBody] = useState(detail.document?.draft?.body || "");
  const [summary, setSummary] = useState("");
  const [busy, setBusy] = useState(false);

  const saveDraft = async () => { await api.put(`/kb/documents/${docId}/draft`, { body, change_summary: summary }); toast.success("Draft saved"); onChange(); };
  const publish = async () => { setBusy(true); try { await api.post(`/kb/documents/${docId}/publish`, { change_summary: summary }); toast.success("Published new version"); onChange(); onListChange(); } finally { setBusy(false); } };
  const assign = async () => { const r = await api.post(`/kb/documents/${docId}/assign`, { scope_type: "tenant" }); toast.success(tl("Assigned to {n}", { n: r.data.recipient_count })); };

  return (
    (<div className="rounded-md border border-border p-3">
      <div className="mb-2 text-xs font-semibold uppercase text-muted-foreground">{t("Author")}</div>
      <Label className="text-xs">{t("Draft body")}</Label>
      <Textarea className="mt-1" value={body} onChange={(e) => setBody(e.target.value)} data-testid="kb-draft-body" />
      <Input className="mt-2" placeholder={t("Change summary")} value={summary} onChange={(e) => setSummary(e.target.value)} data-testid="kb-change-summary" />
      <div className="mt-2 flex flex-wrap gap-2">
        <Button size="sm" variant="outline" onClick={saveDraft} data-testid="kb-save-draft">{t("Save draft")}</Button>
        <Button size="sm" onClick={publish} disabled={busy} data-testid="kb-publish"><Send size={14} className="mr-1" />{t("Publish version")}</Button>
        {canAssign && detail.document?.status === "published" && (
          <Button size="sm" variant="outline" onClick={assign} data-testid="kb-assign">{t("Assign required reading")}</Button>
        )}
      </div>
    </div>)
  );
};

const ComposeDialog: React.FC<{ open: boolean; setOpen: (v: boolean) => void; onDone: () => void }> = ({ open, setOpen, onDone }) => {
  const {
    t: tI18n
  } = useI18n();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [tags, setTags] = useState("");
  const create = async () => {
    if (!title) { toast.error("Title required"); return; }
    await api.post("/kb/documents", { title, description, tags: tags.split(",").map((t) => t.trim()).filter(Boolean), visibility: { scope_type: "tenant" } });
    toast.success("Draft created");
    setOpen(false); setTitle(""); setDescription(""); setTags(""); onDone();
  };
  return (
    (<Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="kb-new-doc"><Plus size={16} className="mr-1.5" />{tI18n("New document")}</Button></DialogTrigger>
      <DialogContent data-testid="kb-compose-dialog">
        <DialogHeader><DialogTitle>{tI18n("New document (draft)")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>{tI18n("Title")}</Label><Input className="mt-1" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="kb-new-title" /></div>
          <div><Label>{tI18n("Description")}</Label><Input className="mt-1" value={description} onChange={(e) => setDescription(e.target.value)} data-testid="kb-new-desc" /></div>
          <div><Label>{tI18n("Tags")}</Label><Input className="mt-1" value={tags} onChange={(e) => setTags(e.target.value)} placeholder={tI18n("comma,separated")} data-testid="kb-new-tags" /></div>
        </div>
        <DialogFooter><Button onClick={create} data-testid="kb-create-doc">{tI18n("Create draft")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>)
  );
};
